import { useRef } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Card, Tag, Space, Button, Tooltip, Empty } from 'antd'
import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import type { UserPermission } from '@/types/permission'
import type { Hospital } from '@/types/hospital'

interface PermissionGridProps {
  permissions: UserPermission[]
  hospitals: Record<string, Hospital>
  onEdit: (permission: UserPermission) => void
  onRevoke: (permission: UserPermission) => void
  loading?: boolean
}

const VIRTUAL_SCROLL_THRESHOLD = 50
const ITEM_HEIGHT = 120

export const PermissionGrid = ({
  permissions,
  hospitals,
  onEdit,
  onRevoke,
  loading = false,
}: PermissionGridProps) => {
  const parentRef = useRef<HTMLDivElement>(null)

  // Use virtual scrolling only if we have many permissions
  const useVirtual = permissions.length > VIRTUAL_SCROLL_THRESHOLD

  const virtualizer = useVirtualizer({
    count: permissions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 5,
    enabled: useVirtual,
  })

  const items = useVirtual ? virtualizer.getVirtualItems() : null

  const renderPermissionCard = (permission: UserPermission) => {
    const hospital = hospitals[permission.hospital_id]
    const flags: Array<{ label: string; color: string }> = [
      permission.admin && { label: 'Admin', color: 'red' },
      permission.sender && { label: 'Sender', color: 'blue' },
      permission.receiver && { label: 'Receiver', color: 'green' },
      permission.readonly && { label: 'Read Only', color: 'default' },
      permission.bi && { label: 'BI', color: 'purple' },
      permission.dashboard && { label: 'Dashboard', color: 'cyan' },
      permission.teamInsights && { label: 'Team Insights', color: 'orange' },
    ].filter(Boolean) as Array<{ label: string; color: string }>

    return (
      <Card
        key={permission.id}
        size="small"
        style={{
          marginBottom: 8,
          ...(useVirtual && {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
          }),
        }}
        actions={[
          <Tooltip key="edit" title="Edit Permissions">
            <Button type="text" icon={<EditOutlined />} onClick={() => onEdit(permission)} />
          </Tooltip>,
          <Tooltip key="revoke" title="Revoke All Permissions">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => onRevoke(permission)}
            />
          </Tooltip>,
        ]}
      >
        <div>
          <div style={{ marginBottom: 8 }}>
            <strong style={{ fontSize: '14px' }}>
              {hospital?.name || `Hospital ${permission.cmsid}`}
            </strong>
          </div>
          {hospital && (
            <div style={{ fontSize: '12px', color: '#888', marginBottom: 8 }}>
              {hospital.city}, {hospital.state} • CMS ID: {hospital.cmsid}
            </div>
          )}
          <Space wrap size={[4, 8]}>
            {flags.map((flag, i) => (
              <Tag key={i} color={flag.color}>
                {flag.label}
              </Tag>
            ))}
          </Space>
        </div>
      </Card>
    )
  }

  // Always use a scrollable container for consistent UX
  const scrollContainerStyle = {
    maxHeight: '500px',
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
    paddingRight: '8px',
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Empty description="Loading permissions..." />
      </div>
    )
  }

  if (permissions.length === 0) {
    return <Empty description="No permissions found" style={{ padding: '40px' }} />
  }

  // Regular rendering for small lists (still wrapped in scroll container)
  if (!useVirtual) {
    return (
      <div style={scrollContainerStyle}>
        {permissions.map((permission) => renderPermissionCard(permission))}
      </div>
    )
  }

  // Virtual scrolling for large lists
  return (
    <div
      ref={parentRef}
      style={{
        height: '500px',
        overflow: 'auto',
      }}
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {items?.map((virtualItem) => {
          const permission = permissions[virtualItem.index]
          return (
            <div
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              {renderPermissionCard(permission)}
            </div>
          )
        })}
      </div>
    </div>
  )
}
